// 羊了个羊游戏逻辑工具函数

// 卡片类型定义
export const CARD_TYPES = [
  '🐑', '🐺', '🐸', '🐰', '🐻', '🐼', '🐨', '🦊', 
  '🐱', '🐶', '🐭', '🐹', '🐷', '🐮', '🐯', '🦁',
  '🐧', '🐦', '🐤', '🐣', '🦆', '🦅', '🦉', '🦇'
];

// 响应式游戏配置
export const getResponsiveGameConfig = () => {
  const screenWidth = window.innerWidth;
  
  if (screenWidth <= 360) {
    // 小屏手机
    return {
      MAX_SELECTED: 7,
      MATCH_COUNT: 3,
      BOARD_WIDTH: 6,      // 进一步减少宽度
      BOARD_HEIGHT: 5,     // 进一步减少高度
      LAYER_COUNT: 3,      // 减少层数
      FILL_RATE: 0.7,
      MIN_CARDS_PER_TYPE: 6,
      CARD_SIZE: 28,
      CARD_SPACING: 32     // 增加间距避免重叠
    };
  } else if (screenWidth <= 480) {
    // 手机
    return {
      MAX_SELECTED: 7,
      MATCH_COUNT: 3,
      BOARD_WIDTH: 8,      // 减少宽度
      BOARD_HEIGHT: 6,     // 减少高度
      LAYER_COUNT: 4,      // 减少层数
      FILL_RATE: 0.7,
      MIN_CARDS_PER_TYPE: 6,
      CARD_SIZE: 32,
      CARD_SPACING: 36     // 增加间距
    };
  } else if (screenWidth <= 768) {
    // 平板
    return {
      MAX_SELECTED: 7,
      MATCH_COUNT: 3,
      BOARD_WIDTH: 9,      // 减少宽度
      BOARD_HEIGHT: 7,     // 减少高度
      LAYER_COUNT: 4,      // 减少层数
      FILL_RATE: 0.7,
      MIN_CARDS_PER_TYPE: 6,
      CARD_SIZE: 40,
      CARD_SPACING: 44     // 增加间距
    };
  } else {
    // 桌面端
    return {
      MAX_SELECTED: 7,
      MATCH_COUNT: 3,
      BOARD_WIDTH: 12,     // 完整宽度
      BOARD_HEIGHT: 8,     // 完整高度
      LAYER_COUNT: 6,      // 完整层数
      FILL_RATE: 0.7,
      MIN_CARDS_PER_TYPE: 6,
      CARD_SIZE: 55,
      CARD_SPACING: 60
    };
  }
};

// 默认游戏配置（保持向后兼容）
export const GAME_CONFIG = {
  MAX_SELECTED: 7,      // 最大选择数量
  MATCH_COUNT: 3,       // 匹配数量
  BOARD_WIDTH: 12,      // 游戏板宽度
  BOARD_HEIGHT: 8,      // 游戏板高度
  LAYER_COUNT: 6,       // 层数
  FILL_RATE: 0.7,       // 填充率
  MIN_CARDS_PER_TYPE: 6, // 每种卡片的最小数量
};

/**
 * 生成随机卡片类型
 */
export const getRandomCardType = () => {
  return CARD_TYPES[Math.floor(Math.random() * CARD_TYPES.length)];
};

/**
 * 生成保证有解的卡片布局
 */
export const generateSolvableCards = () => {
  const config = getResponsiveGameConfig();
  const { BOARD_WIDTH, BOARD_HEIGHT, LAYER_COUNT, FILL_RATE, MIN_CARDS_PER_TYPE } = config;
  const cards = [];
  const totalSlots = BOARD_WIDTH * BOARD_HEIGHT * LAYER_COUNT;
  const totalCards = Math.floor(totalSlots * FILL_RATE);
  
  // 确保卡片数量为3的倍数
  const adjustedTotalCards = Math.floor(totalCards / 3) * 3;
  
  // 生成卡片类型分布
  const cardTypeCounts = {};
  let remainingCards = adjustedTotalCards;
  
  // 先确保每种类型至少有最小数量
  const selectedTypes = CARD_TYPES.slice(0, Math.min(8, CARD_TYPES.length));
  selectedTypes.forEach(type => {
    const count = Math.floor(MIN_CARDS_PER_TYPE / 3) * 3;
    cardTypeCounts[type] = count;
    remainingCards -= count;
  });
  
  // 随机分配剩余卡片
  while (remainingCards > 0) {
    const type = selectedTypes[Math.floor(Math.random() * selectedTypes.length)];
    const addCount = Math.min(3, remainingCards);
    cardTypeCounts[type] += addCount;
    remainingCards -= addCount;
  }
  
  // 生成卡片
  let cardId = 0;
  Object.entries(cardTypeCounts).forEach(([type, count]) => {
    for (let i = 0; i < count; i++) {
      cards.push({
        id: `card_${cardId++}`,
        type,
        position: { x: 0, y: 0, z: 0 },
        selectable: false,
        selected: false
      });
    }
  });
  
  // 随机分配位置
  shuffleArray(cards);
  cards.forEach((card, index) => {
    const position = generateRandomPosition(BOARD_WIDTH, BOARD_HEIGHT, LAYER_COUNT);
    card.position = position;
  });
  
  return cards;
};

/**
 * 生成随机位置
 */
const generateRandomPosition = (width, height, layers) => {
  return {
    x: Math.floor(Math.random() * width),
    y: Math.floor(Math.random() * height),
    z: Math.floor(Math.random() * layers)
  };
};

/**
 * 数组随机打乱
 */
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * 更新卡片可选择状态
 */
export const updateSelectableState = (cards) => {
  cards.forEach(card => {
    if (card.selected) {
      card.selectable = false;
      return;
    }
    
    // 检查是否被其他卡片遮挡
    const isBlocked = cards.some(otherCard => {
      if (otherCard.id === card.id || otherCard.selected) return false;
      
      // 如果其他卡片在更高层且位置重叠或相邻
      return (
        otherCard.position.z > card.position.z &&
        Math.abs(otherCard.position.x - card.position.x) <= 1 &&
        Math.abs(otherCard.position.y - card.position.y) <= 1
      );
    });
    
    card.selectable = !isBlocked;
  });
  
  return cards;
};

/**
 * 检查是否有可消除的三张相同卡片
 */
export const findMatchableCards = (selectedCards) => {
  const cardCounts = {};
  
  selectedCards.forEach(card => {
    cardCounts[card.type] = (cardCounts[card.type] || 0) + 1;
  });
  
  return Object.entries(cardCounts)
    .filter(([type, count]) => count >= GAME_CONFIG.MATCH_COUNT)
    .map(([type]) => type);
};

/**
 * 移除匹配的卡片
 */
export const removeMatchedCards = (selectedCards, matchedTypes) => {
  let updatedCards = [...selectedCards];
  
  matchedTypes.forEach(type => {
    let removedCount = 0;
    updatedCards = updatedCards.filter(card => {
      if (card.type === type && removedCount < GAME_CONFIG.MATCH_COUNT) {
        removedCount++;
        return false;
      }
      return true;
    });
  });
  
  return updatedCards;
};

/**
 * 检查游戏是否可以继续
 */
export const canGameContinue = (cards, selectedCards) => {
  // 如果所有卡片都被选择了，游戏胜利
  const remainingCards = cards.filter(card => !card.selected);
  if (remainingCards.length === 0) {
    return { canContinue: false, reason: 'won' };
  }
  
  // 如果选择区域已满且无法消除，游戏失败
  if (selectedCards.length >= GAME_CONFIG.MAX_SELECTED) {
    const matchableTypes = findMatchableCards(selectedCards);
    if (matchableTypes.length === 0) {
      return { canContinue: false, reason: 'lost' };
    }
  }
  
  // 检查是否还有可选择的卡片
  const selectableCards = remainingCards.filter(card => card.selectable);
  if (selectableCards.length === 0 && selectedCards.length === 0) {
    return { canContinue: false, reason: 'stuck' };
  }
  
  return { canContinue: true };
};

/**
 * 获取游戏提示
 */
export const getGameHint = (cards, selectedCards) => {
  // 检查选择区域是否有可以立即消除的卡片
  const matchableTypes = findMatchableCards(selectedCards);
  if (matchableTypes.length > 0) {
    return {
      type: 'match_available',
      message: `可以消除 ${matchableTypes.join(', ')}`
    };
  }
  
  // 寻找场上与选择区域中卡片相同的可选择卡片
  const selectedTypes = selectedCards.map(card => card.type);
  const availableCards = cards.filter(card => !card.selected && card.selectable);
  
  for (const selectedType of selectedTypes) {
    const sameTypeCards = availableCards.filter(card => card.type === selectedType);
    if (sameTypeCards.length > 0) {
      return {
        type: 'similar_available',
        message: `场上还有 ${selectedType} 可以选择`
      };
    }
  }
  
  // 提示选择新的卡片类型
  const availableTypes = [...new Set(availableCards.map(card => card.type))];
  if (availableTypes.length > 0) {
    return {
      type: 'new_type_suggestion',
      message: `可以尝试选择 ${availableTypes.slice(0, 3).join(', ')}`
    };
  }
  
  return {
    type: 'no_hint',
    message: '暂无可用提示'
  };
};

/**
 * 计算得分
 */
export const calculateScore = (matchedTypes, currentScore, level = 1) => {
  const baseScore = 100;
  const levelMultiplier = level;
  const typeBonus = matchedTypes.length * 50;
  
  return currentScore + (baseScore * levelMultiplier + typeBonus);
};

/**
 * 生成关卡配置
 */
export const generateLevelConfig = (level) => {
  return {
    boardWidth: Math.min(12, 8 + level),
    boardHeight: Math.min(10, 6 + level),
    layerCount: Math.min(8, 4 + Math.floor(level / 2)),
    fillRate: Math.min(0.8, 0.5 + level * 0.05),
    cardTypeCount: Math.min(16, 6 + level),
    timeLimit: Math.max(180, 300 - level * 10) // 秒
  };
};
